<template>
  <section
    class="latest-auctions-with-category"
    :aria-busy="isLoading"
  >
    <header
      v-if="title || productsNo"
      class="latest-auctions-with-category__header"
    >
      <span v-if="title" class="header-chip">
        {{ title }}
      </span>
      <span v-if="productsNo" class="header-count">
        {{ productsNo }}
        {{ productsNo === 1 ? "Product" : "Products" }}
      </span>
    </header>

    <div
      v-if="isLoading"
      class="latest-auctions-with-category__grid"
      aria-hidden="true"
    >
      <div
        v-for="skeletonIndex in skeletonItems"
        :key="skeletonIndex"
        class="product-card product-card--skeleton"
      >
        <div class="product-card__media skeleton-block" />
        <div class="product-card__footer">
          <span class="skeleton-line skeleton-line--title" />
        </div>
      </div>
    </div>

    <div
      v-else-if="getLimitedAuctions.length"
      class="latest-auctions-with-category__grid"
    >
      <article
        v-for="product in getLimitedAuctions"
        :key="product._id"
        class="product-card"
      >
        <LinkWrapper
          class="product-card__link"
          :location="productURL(product)"
        >
          <div class="product-card__media">
            <img
              v-if="productImage(product)"
              :src="productImage(product)"
              :alt="productName(product)"
            />
            <div v-else class="product-card__media--placeholder">
              <span>No image</span>
            </div>
          </div>
          <div class="product-card__footer">
            <span class="product-card__title">
              {{ productName(product) }}
            </span>
          </div>
        </LinkWrapper>
      </article>
    </div>

    <div v-else class="latest-auctions-with-category__empty">
      <p>Products are on their way. Please check back soon.</p>
    </div>

    <LinkWrapper
      v-if="ctaLocation"
      class="latest-auctions-with-category__cta"
      :location="ctaLocation"
    >
      View all Products
    </LinkWrapper>
  </section>
</template>
<script>
import { getAllProductsAndVariants } from "../../../composables/manageProducts";
import LinkWrapper from "../../atoms/LinkWrapper/LinkWrapper.vue";

export default {
  components: { LinkWrapper },
  props: {
    categoryId: {
      type: String,
      default: null,
    },
    maxAuctions: {
      type: Number,
      default: 4,
    },
    title: {
      type: String
    }, 
    productsNo: {
      type: Number
    },
    skipAuctions: {
      type: Number,
      default: 0
    },
    background: {
      type: String
    }
  },
  data() {
    return {
      auctions: [],
      isLoading: true,
    };
  },
  watch: {
    categoryId: {
      handler() {
        this.getProducts();
      },
      immediate: true,
    },
    maxAuctions() {
      this.getProducts();
    },
    skipAuctions() {
      this.getProducts();
    },
  },
  computed: {
    getLimitedAuctions() {
      return this.auctions.slice(this.skipAuctions,this.maxAuctions + this.skipAuctions);
    },
    skeletonItems() {
      const desired = Number.isFinite(this.maxAuctions) && this.maxAuctions > 0
        ? this.maxAuctions
        : 4;

      return Math.max(desired, 4);
    },
    ctaLocation() {
      return this.categoryId
        ? `/shop?categoryId=${this.categoryId}`
        : "/shop";
    },
  },
  methods: {
    productImage(product) {
      return product?.immagine?.[0]?.path || "";
    },
    productName(product) {
      return product?._prodotto?.[0]?.nome || "Untitled product";
    },
    productURL(product) {
      const productId = product?._prodotto?.[0]?._id;
      const variantId = product?._id;
      return productId && variantId
        ? this.$buildProductURL(productId, variantId)
        : "/shop";
    },
    async getProducts() {
      this.isLoading = true;

      try {
        const limit =
          typeof this.maxAuctions === "number" && this.maxAuctions > 0
            ? this.maxAuctions + this.skipAuctions
            : 4;

        const queryParams = {
          limit,
          page: 1,
        };

        if (this.categoryId) {
          queryParams.categoryId = this.categoryId;
        }

        const res = await getAllProductsAndVariants(queryParams);

        this.auctions = Array.isArray(res?.varianti) ? res.varianti : [];
      } catch (error) {
        console.error("Failed to load products", error);
        this.auctions = [];
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped="scoped">
.latest-auctions-with-category {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.latest-auctions-with-category__header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-chip {
  background: #000;
  color: #fff;
  border-radius: 999px;
  padding: 10px 20px;
  font-weight: 600;
  font-size: 16px;
}

.header-count {
  color: #8c8c8c;
  font-size: 16px;
}

.latest-auctions-with-category__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 28px;
}

.product-card {
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e2e2e2;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.product-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.12);
}

.product-card__link {
  display: flex;
  flex-direction: column;
  height: 100%;
  color: inherit;
  text-decoration: none;
}

.product-card__media {
  position: relative;
  padding-top: 62%;
  overflow: hidden;
  background: #f2f2f2;
}

.product-card__media img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-card__media--placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #888;
}

.product-card__footer {
  padding: 16px 18px;
  border-top: 1px solid #dcdcdc;
}

.product-card__title {
  font-weight: 600;
  font-size: 18px;
  color: #141414;
  display: block;
}

.latest-auctions-with-category__empty {
  text-align: center;
  color: #696969;
}

.latest-auctions-with-category__cta {
  align-self: center;
  padding: 12px 28px;
  border-radius: 999px;
  background: #000;
  color: #fff;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease;
}

.latest-auctions-with-category__cta:hover {
  background: #1a1a1a;
  color: #fff;
}

.product-card--skeleton {
  pointer-events: none;
  box-shadow: none;
}

.skeleton-block {
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(230, 230, 230, 0.9) 25%,
    rgba(245, 245, 245, 0.9) 50%,
    rgba(230, 230, 230, 0.9) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
  border-radius: 0;
}

.skeleton-line {
  display: block;
  height: 18px;
  border-radius: 12px;
  background: linear-gradient(
    90deg,
    rgba(230, 230, 230, 0.9) 25%,
    rgba(245, 245, 245, 0.9) 50%,
    rgba(230, 230, 230, 0.9) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
}

.skeleton-line--title {
  width: 70%;
  height: 20px;
  display: inline-block;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 767.98px) {
  .latest-auctions-with-category {
    gap: 24px;
  }

  .latest-auctions-with-category__grid {
    gap: 20px;
  }

  .product-card__footer {
    padding: 14px;
  }

  .product-card__title {
    font-size: 16px;
  }

  .header-chip {
    padding: 8px 16px;
    font-size: 14px;
  }
}
</style>
